import { Grid, Typography } from "@material-ui/core";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import {
  SERVICE_TYPE,
  TGetServicesCollectionItem,
} from "../../../../../../api/service/service.types";
import { formatCurrency } from "../../../../../../util/formatNumbers";
import { serviceCommercialNotes } from "../../util/service.util";
import InfoBox from "./InfoBox";
import InfoBoxItem from "./InfoBoxitem";

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
`;

const StyledTitle = styled(Typography)`
  font-size: 1.25rem;
  font-weight: 700;
  padding: 1rem;
`;

const StyledContent = styled.div`
  flex-grow: 1;
  overflow-x: hidden;
  overflow-y: scroll;
  box-sizing: border-box;
  padding: 0 1rem 1rem;

  ::-webkit-scrollbar {
    width: 0.375rem;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: rgba(0, 72, 120, 0.25);
    width: 0.375rem;
    border-radius: 0.25rem;
  }
`;

interface IServiceInfoPanelProps {
  service: TGetServicesCollectionItem;
}

const ServiceInfoPanel = (props: IServiceInfoPanelProps) => {
  const { service } = props;
  const { formatMessage } = useIntl();

  let clientName = "";
  if (service.customer?.First_Name) {
    clientName += service.customer.First_Name + " ";
  }
  if (service.customer?.Last_Name) {
    clientName += service.customer.Last_Name;
  }

  return (
    <StyledWrapper>
      <StyledTitle>
        <FormattedMessage
          id="ServiceInfoPanel.Title"
          defaultMessage="Información"
        />
      </StyledTitle>
      <StyledContent>
        <InfoBox
          summary={
            <Typography variant="h4">
              <FormattedMessage
                id="ServiceInfoPanel.Features"
                defaultMessage="Características"
              />
            </Typography>
          }
          details={
            <Grid container spacing={8}>
              <Grid item xs={12} md={12}>
                <InfoBoxItem
                  label={formatMessage({
                    id: "ServiceInfoPanel.ServiceType",
                    defaultMessage: "Tipología de servicio",
                  })}
                  data={service.Tipo_de_Servicio}
                />
              </Grid>
              {service.Tipo_de_Servicio === SERVICE_TYPE.SELECTION && (
                <Grid item xs={12} md={12}>
                  <InfoBoxItem
                    label={formatMessage({
                      id: "ServiceInfoPanel.Type",
                      defaultMessage: "Tipología",
                    })}
                    data={service.Tipo_de_Trato}
                  />
                </Grid>
              )}
              {service.Tipo_de_Servicio === SERVICE_TYPE.SELECTION && (
                <Grid item xs={12} md={6}>
                  <InfoBoxItem
                    label={formatMessage({
                      id: "ServiceInfoPanel.Punctual",
                      defaultMessage: "Servicio Puntual",
                    })}
                    data={service.Servicio_corta_duracion}
                  />
                </Grid>
              )}
              {service.Tipo_de_Servicio === SERVICE_TYPE.SELECTION && (
                <Grid item xs={12} md={6}>
                  <InfoBoxItem
                    label={formatMessage({
                      id: "ServiceInfoPanel.Umbrella",
                      defaultMessage: "Servicio Paraguas",
                    })}
                    data={service.Servicio_Paraguas}
                  />
                </Grid>
              )}
            </Grid>
          }
        />
        <InfoBox
          summary={
            <Typography variant="h4">
              <FormattedMessage
                id="ServiceInfoPanel.ClientData"
                defaultMessage="Datos de Cliente"
              />
            </Typography>
          }
          details={
            <Grid container spacing={8}>
              <Grid item xs={12} md={12}>
                <InfoBoxItem
                  label={formatMessage({
                    id: "ServiceInfoPanel.Client",
                    defaultMessage: "Cliente",
                  })}
                  data={clientName.trim()}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <InfoBoxItem
                  label={formatMessage({
                    id: "ServiceInfoPanel.ClientEmail",
                    defaultMessage: "Email del Cliente",
                  })}
                  data={service.customer?.Email}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoBoxItem
                  label={formatMessage({
                    id: "ServiceInfoPanel.ClientPhone",
                    defaultMessage: "Teléfono del Cliente",
                  })}
                  data={service.Telefono_cliente}
                />
              </Grid>
            </Grid>
          }
        />
        <InfoBox
          summary={
            <Typography variant="h4">
              <FormattedMessage
                id="ServiceInfoPanel.Commercial"
                defaultMessage="Comercial"
              />
            </Typography>
          }
          details={
            <Grid container spacing={8}>
              {service.Venta_relacionada?.ownerName && (
                <Grid item xs={12}>
                  <InfoBoxItem
                    label={formatMessage({
                      id: "ServiceInfoPanel.DealOwner",
                      defaultMessage: "Propietario Oportunidad",
                    })}
                    data={service.Venta_relacionada.ownerName}
                  />
                </Grid>
              )}
              {service.Tipo_de_Servicio === SERVICE_TYPE.SELECTION && (
                <Grid item xs={12}>
                  <InfoBoxItem
                    label={formatMessage({
                      id: "ServiceInfoPanel.ScheduleOnSale",
                      defaultMessage: "Horario servicio (Venta)",
                    })}
                    data={service.Horario_servicio_venta}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <InfoBoxItem
                  label={formatMessage({
                    id: "ServiceInfoPanel.CommercialNotes",
                    defaultMessage: "Notas dpto. Comercial",
                  })}
                  data={serviceCommercialNotes(service) ?? undefined}
                />
              </Grid>
              <Grid item xs={12}>
                <InfoBoxItem
                  label={formatMessage({
                    id: "ServiceInfoPanel.Observations",
                    defaultMessage: "Observaciones",
                  })}
                  data={service.Observaciones1}
                />
              </Grid>
              <Grid item xs={12}>
                <InfoBoxItem
                  label={formatMessage({
                    id: "ServiceInfoPanel.OtherContactData",
                    defaultMessage: "Otros datos de contacto",
                  })}
                  data={service.Otros_contactos}
                />
              </Grid>
              {service.Tipo_de_Servicio === SERVICE_TYPE.SELECTION && (
                <Grid item xs={6}>
                  <InfoBoxItem
                    label={formatMessage({
                      id: "ServiceInfoPanel.GrossSalarySale",
                      defaultMessage: "Salario Bruto",
                    })}
                    data={
                      service.Salario_bruto_ventas
                        ? formatCurrency(service.Salario_bruto_ventas)
                        : "-"
                    }
                  />
                </Grid>
              )}
              {service.Tipo_de_Servicio === SERVICE_TYPE.SELECTION && (
                <Grid item xs={6}>
                  <InfoBoxItem
                    label={formatMessage({
                      id: "ServiceInfoPanel.NetSalarySale",
                      defaultMessage: "Salario Neto",
                    })}
                    data={
                      service.Salario_neto_ventas
                        ? formatCurrency(service.Salario_neto_ventas)
                        : "-"
                    }
                  />
                </Grid>
              )}
              {service.Tipo_de_Servicio === SERVICE_TYPE.SELECTION && (
                <Grid item xs={6}>
                  <InfoBoxItem
                    label={formatMessage({
                      id: "ServiceInfoPanel.Salario diario",
                      defaultMessage: "Salario diario",
                    })}
                    data={
                      service.Sueldo_d_a_trabajador
                        ? formatCurrency(service.Sueldo_d_a_trabajador)
                        : "-"
                    }
                  />
                </Grid>
              )}
              {service.Tipo_de_Servicio === SERVICE_TYPE.SELECTION && (
                <Grid item xs={6}>
                  <InfoBoxItem
                    label={formatMessage({
                      id: "ServiceInfoPanel.SocialSecuritySale",
                      defaultMessage: "Seguridad Social",
                    })}
                    data={
                      service.Seguridad_social_ventas
                        ? formatCurrency(service.Seguridad_social_ventas)
                        : "-"
                    }
                  />
                </Grid>
              )}
            </Grid>
          }
        />
        {service.Tipo_de_Servicio === SERVICE_TYPE.SELECTION && (
          <InfoBox
            summary={
              <Typography variant="h4">
                <FormattedMessage
                  id="ServiceInfoPanel.Incentives"
                  defaultMessage="Incentivos"
                />
              </Typography>
            }
            details={
              <Grid container spacing={8}>
                <Grid item xs={12}>
                  <InfoBoxItem
                    label={formatMessage({
                      id: "ServiceInfoPanel.IncentivesDetail",
                      defaultMessage: "Detalle incentivos",
                    })}
                    data={service.Detalle_incentivos}
                  />
                </Grid>
              </Grid>
            }
          />
        )}
        <InfoBox
          summary={
            <Typography variant="h4">
              <FormattedMessage
                id="ServiceInfoPanel.EconomicConditions"
                defaultMessage="Condiciones económicas"
              />
            </Typography>
          }
          details={
            <Grid container spacing={8}>
              <Grid item xs={12} md={4}>
                <InfoBoxItem
                  label={formatMessage({
                    id: "ServiceInfoPanel.SignUpFee",
                    defaultMessage: "Cuota Alta",
                  })}
                  data={
                    service.Cuota_Alta
                      ? formatCurrency(parseFloat(service.Cuota_Alta))
                      : "-"
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoBoxItem
                  label={formatMessage({
                    id: "ServiceInfoPanel.MonthlyFee",
                    defaultMessage: "Cuota Cuideo",
                  })}
                  data={
                    service.Cuota_Cuideo
                      ? formatCurrency(parseFloat(service.Cuota_Cuideo))
                      : "-"
                  }
                />
              </Grid>
              {service.Tipo_de_Servicio === SERVICE_TYPE.SELECTION && (
                <Grid item xs={12} md={4}>
                  <InfoBoxItem
                    label={formatMessage({
                      id: "ServiceInfoPanel.TotalMonthlyEmployeeCost",
                      defaultMessage: "Total coste mensual trabajador",
                    })}
                    data={
                      service.Total_coste_mensual_trabajador
                        ? formatCurrency(
                            parseFloat(service.Total_coste_mensual_trabajador)
                          )
                        : "-"
                    }
                  />
                </Grid>
              )}
            </Grid>
          }
        />
      </StyledContent>
    </StyledWrapper>
  );
};

export default ServiceInfoPanel;
